import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";
import { classes as staticClasses } from "@/data/classes";
import {
  Users,
  BookOpen,
  GraduationCap,
  Store,
  FileText,
  Ticket,
  DollarSign,
  Mail,
} from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Hit {
  id: string;
  type: string;
  label: string;
  sub?: string;
  href: string;
}

function useDebounced<T>(value: T, delay = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function UniversalSearch({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, Hit[]>>({});
  const debouncedQ = useDebounced(q, 200);

  // Static class matches — instant
  const classHits: Hit[] = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return staticClasses
      .filter((c) => c.title.toLowerCase().includes(term))
      .slice(0, 8)
      .map((c) => ({
        id: c.id,
        type: "class",
        label: c.title,
        sub: c.category,
        href: `/admin/classes?focus=${encodeURIComponent(c.id)}`,
      }));
  }, [q]);

  useEffect(() => {
    const term = debouncedQ.trim();
    if (!open || term.length < 2) {
      setResults({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    const like = `%${term}%`;
    (async () => {
      const [payments, interest, vendors, tickets, proposals, instructors, dbClasses, emails] = await Promise.all([
        supabase
          .from("class_payments")
          .select("id,adult_name,email,child_name,class_id")
          .or(`adult_name.ilike.${like},email.ilike.${like},child_name.ilike.${like}`)
          .limit(6),
        supabase
          .from("class_interest")
          .select("id,adult_name,email,child_name,class_id")
          .or(`adult_name.ilike.${like},email.ilike.${like},child_name.ilike.${like}`)
          .limit(6),
        supabase
          .from("popup_vendor_registrations")
          .select("id,full_name,email,vendor_option")
          .or(`full_name.ilike.${like},email.ilike.${like}`)
          .limit(6),
        supabase
          .from("popup_early_bird_orders")
          .select("id,full_name,email")
          .or(`full_name.ilike.${like},email.ilike.${like}`)
          .limit(6),
        supabase
          .from("instructor_proposals")
          .select("id,full_name,email,proposed_class_name,review_status")
          .or(`full_name.ilike.${like},email.ilike.${like},proposed_class_name.ilike.${like}`)
          .limit(6),
        supabase
          .from("instructors")
          .select("id,full_name,email,status,merged_into_id")
          .or(`full_name.ilike.${like},email.ilike.${like}`)
          .limit(6),
        supabase
          .from("classes")
          .select("id,title,status")
          .ilike("title", like)
          .limit(6),
        supabase
          .from("email_send_log")
          .select("id,recipient_email,template_name,status")
          .ilike("recipient_email", like)
          .limit(6),
      ]);
      if (cancelled) return;

      const next: Record<string, Hit[]> = {};

      if (classHits.length) next["Classes (Catalog)"] = classHits;

      if (dbClasses.data?.length) {
        next["Classes (Database)"] = dbClasses.data.map((c: any) => ({
          id: c.id,
          type: "db_class",
          label: c.title,
          sub: c.status ?? "",
          href: `/admin/classes?focus=${encodeURIComponent(c.id)}`,
        }));
      }

      if (payments.data?.length) {
        next["Paid Registrations"] = payments.data.map((p: any) => ({
          id: p.id,
          type: "payment",
          label: `${p.adult_name || p.email}`,
          sub: `${p.child_name ?? ""} · ${p.email}`,
          href: `/admin/registrations?focusPayment=${encodeURIComponent(p.id)}`,
        }));
      }

      if (interest.data?.length) {
        next["Interest Forms"] = interest.data.map((s: any) => ({
          id: s.id,
          type: "interest",
          label: `${s.adult_name || s.email}`,
          sub: `${s.child_name ?? ""} · ${s.email}`,
          href: `/admin/registrations?focusInterest=${encodeURIComponent(s.id)}`,
        }));
      }

      if (vendors.data?.length) {
        next["Pop-Up Vendors"] = vendors.data.map((v: any) => ({
          id: v.id,
          type: "vendor",
          label: v.full_name || v.email,
          sub: `${v.email} · ${v.vendor_option ?? ""}`,
          href: `/admin/popup?focus=vendor:${v.id}`,
        }));
      }

      if (tickets.data?.length) {
        next["Early Bird Tickets"] = tickets.data.map((t: any) => ({
          id: t.id,
          type: "ticket",
          label: t.full_name || t.email,
          sub: t.email,
          href: `/admin/popup?focus=ticket:${t.id}`,
        }));
      }

      if (proposals.data?.length) {
        next["Instructor Proposals"] = proposals.data.map((p: any) => ({
          id: p.id,
          type: "proposal",
          label: p.full_name,
          sub: `${p.proposed_class_name ?? ""} · ${p.review_status ?? ""}`,
          href: `/admin/instructors?focus=proposal:${p.id}`,
        }));
      }

      if (instructors.data?.length) {
        next["Instructors"] = instructors.data.map((i: any) => {
          const targetId = i.merged_into_id ?? i.id;
          const label = i.merged_into_id ? `${i.full_name} → merged` : i.full_name;
          return {
            id: i.id,
            type: "instructor",
            label,
            sub: `${i.email ?? ""} · ${i.status ?? ""}`,
            href: `/admin/instructors?focus=instructor:${targetId}`,
          };
        });
      }

      if (emails.data?.length) {
        next["Emails"] = emails.data.map((e: any) => ({
          id: e.id,
          type: "email",
          label: e.recipient_email,
          sub: `${e.template_name} · ${e.status}`,
          href: `/admin?focus=email:${e.id}`,
        }));
      }

      setResults(next);
      setLoading(false);
    })().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, open, classHits]);

  const iconFor = (type: string) => {
    switch (type) {
      case "class":
      case "db_class":
        return <BookOpen className="h-4 w-4" />;
      case "payment":
        return <DollarSign className="h-4 w-4" />;
      case "interest":
        return <Users className="h-4 w-4" />;
      case "vendor":
        return <Store className="h-4 w-4" />;
      case "ticket":
        return <Ticket className="h-4 w-4" />;
      case "proposal":
      case "instructor":
        return <GraduationCap className="h-4 w-4" />;
      case "email":
        return <Mail className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const groups = Object.entries(results);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search parents, students, classes, instructors, vendors, emails…"
        value={q}
        onValueChange={setQ}
      />
      <CommandList>
        {q.trim().length < 2 ? (
          <CommandEmpty>Type at least 2 characters to search.</CommandEmpty>
        ) : loading && groups.length === 0 ? (
          <CommandEmpty>Searching…</CommandEmpty>
        ) : groups.length === 0 ? (
          <CommandEmpty>Nothing matches that yet.</CommandEmpty>
        ) : (
          groups.map(([label, hits], idx) => (
            <div key={label}>
              {idx > 0 && <CommandSeparator />}
              <CommandGroup heading={`${label} (${hits.length})`}>
                {hits.map((h) => (
                  <CommandItem
                    key={`${h.type}-${h.id}`}
                    value={`${label}-${h.id}-${h.label}`}
                    onSelect={() => {
                      onOpenChange(false);
                      navigate(h.href);
                    }}
                    className="flex items-start gap-2"
                  >
                    <span className="mt-0.5 text-muted-foreground">{iconFor(h.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{h.label}</p>
                      {h.sub && <p className="text-xs text-muted-foreground truncate">{h.sub}</p>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          ))
        )}
      </CommandList>
    </CommandDialog>
  );
}
